import React, { useEffect, useState } from 'react';
import { useAuth } from '../../api/auth/store';
import MpinInput from '../../components/Auth/MpinInput';
import EditorControl from '../../components/EditorControl';
import CStack from '../../components/CStack';
import SwitchControl from '../../components/SwitchControl';
import ButtonControl from '../../components/ButtonControl';
import useUXStore from '../../api/ux/store';
import { DialogInfo } from '../../api/ux/data';
import usePasswordConfirmation from '../../hooks/usePasswordConfirmation';
import PasskeyManagement from '../../components/Auth/PasskeyManagement';
import { updatePasskeySettings, checkPasskeySupport, completePasskeyRegistration, isOk } from '../../api/auth/passkey_actions';
import { getDeviceName } from '../../utils/webauthn';
import { ERR_INVALID_CRED } from '../../constants';
import { ReactComponent as KeyIcon } from '../../svgs/key.svg';
import { DialpadIcon, EditIcon, SlidersIcon, GearIcon } from '../../components/icons/SettingsIcons';

const MFA_PIN_LENGTH = 6; // Define the PIN length as a constant

const MfaSettingsSection: React.FC = () => {
    const {
        mfaSettings,
        mfaSettingsLoading,
        mfaSettingsError,
        fetchMfaSettings,
        setupMpin,
        updateMfaToggles
    } = useAuth();

    const { refreshDialog, hideDialog, addToast } = useUXStore();
    const { confirm: confirmPassword } = usePasswordConfirmation();
    const [mpinToSet, setMpinToSet] = useState('');
    const [mpinError, setMpinError] = useState<string | null>(null);
    const [showPasskeyManagement, setShowPasskeyManagement] = useState(false);
    const [passkeySupport] = useState(() => checkPasskeySupport());

    useEffect(() => {
        fetchMfaSettings();
    }, [fetchMfaSettings]);

    const dialogId = "mpin-setup-dialog"; // Unique ID for the MPIN dialog

    const handleCloseMpinDialog = () => {
        hideDialog(dialogId);
        setMpinToSet(''); // Clear input on close
        setMpinError(null); // Clear error on close
        fetchMfaSettings(); // Refetch settings to reflect changes
    };

    const handleMpinFinish = async (pin: string, password: string) => {
        handleCloseMpinDialog(); // Close dialog on successful setup
        setMpinError(null); // Clear any previous errors
        const response = await setupMpin(pin, password);
        console.log(response)
        if (response.Code === 200) { // Check for string "200" for success
            setMpinToSet(''); // Clear the input after successful setup
            addToast("", "MPIN changed successfully.")
        } else if (response.Code === ERR_INVALID_CRED) {
            // If the response indicates invalid credentials, show an error
            setMpinError("Invalid credentials. Please try again.");
            addToast("Error", "Invalid credentials. Please try again.");
        } else {
            // If it's an error response, Msg will be a string
            setMpinError(response.Msg || "Failed to set MPIN.");
            addToast("", "MPIN changed failed.")
        }
    };

    const handleVerificationMismatch = () => {
        setMpinError("MPINs do not match. Please try again.");
        setMpinToSet(''); // Clear the input for re-entry
        refreshDialog({
            title: "Retry",
            id: dialogId
        })
    };

    const handleVerificationSuccess = () => {
        setMpinError(null); // Clear error on successful verification
    };

    const handleOpenMpinDialog = () => {
        const title = mfaSettings?.hasPinSetup ? "Change MPIN" : "Set MPIN";
        const message = `Enter password to ${mfaSettings?.hasPinSetup ? 'change' : 'set'} your MPIN.`;

        confirmPassword(async (password: string) => {
            // After password is confirmed, show the MPIN setup dialog
            setMpinToSet(''); // Clear any previous input
            setMpinError(null); // Clear any previous errors

            const dialogContent = (
                <div style={{ marginBottom: 25, marginTop: 25 }}>
                    <MpinInput
                        pinLength={MFA_PIN_LENGTH}
                        onChange={setMpinToSet} // Update local state as digits are entered
                        onFinish={(pin: string) => handleMpinFinish(pin, password)} // Pass password to finish handler
                        label="Enter New MPIN"
                        verifyMode={true} // Enable verification mode
                        onVerificationMismatch={handleVerificationMismatch}
                        onVerificationSuccess={handleVerificationSuccess}
                        enabled={true} // Always enabled when dialog is open
                        onVerificationStepChanged={(step) => {
                            refreshDialog({
                                title: step === 1 ? "Verify MPIN" : title,
                                id: dialogId
                            })
                        }}
                    />
                    {mpinError && <p style={{ color: 'red', marginTop: '10px' }}>{mpinError}</p>}
                </div>
            );

            const dialogInfo: DialogInfo = {
                id: dialogId,
                title: title,
                content: dialogContent,
                visible: true,
                onClose: handleCloseMpinDialog,
                onConfirm: undefined, // Confirm button will just close the dialog after MPIN is set
                confirmContent: "Done", // Changed from confirmText to confirmContent
                confirmActive: true,
            };
            refreshDialog(dialogInfo);
        }, {
            title: "Security Verification",
            message: message
        });
    };

    // When passkeys are enabled but none is registered yet, let the user nominate one immediately
    // (password confirm → system passkey prompt) instead of routing through the management dialog.
    const handleRegisterPasskey = () => {
        confirmPassword(async (password: string) => {
            const deviceName = getDeviceName();
            const result = await completePasskeyRegistration(deviceName, password);
            if (isOk(result.Code)) {
                addToast("", "Passkey registered successfully.");
                await fetchMfaSettings(); // Refresh so hasPasskeySetup flips and the button becomes "Manage"
            } else {
                addToast("Error", typeof result.Msg === "string" ? result.Msg : "Failed to register passkey.");
            }
        }, {
            title: "Register Passkey",
            message: "Please confirm your password to register a new passkey."
        });
    };

    const handleOpenPasskeyManagement = () => {
        const passkeyDialogId = "passkey-management-dialog";

        const dialogContent = (
            <div style={{ marginBottom: 25, marginTop: 25 }}>
                <PasskeyManagement
                    onError={(error) => addToast("Error", error)}
                    onSuccess={(message) => addToast("", message)}
                />
            </div>
        );

        const dialogInfo: DialogInfo = {
            id: passkeyDialogId,
            title: "Manage Passkeys",
            content: dialogContent,
            visible: true,
            onClose: () => hideDialog(passkeyDialogId),
            onConfirm: undefined,
            confirmContent: "Close",
            confirmActive: true,
        };

        refreshDialog(dialogInfo);
    };

    const handleApply = async (data: any) => {
        console.log("Applying MFA settings:", data);

        // Build the payload based on what has changed
        const payload: any = {};

        // Two-factor login enforcement is "coming soon"; only the method toggles are applied.
        payload.enable_pin = data.isPinEnabled;
        payload.enable_passkey = data.isPasskeyEnabled;

        // Only make API call if something has changed
        if (Object.keys(payload).length > 0) {
            return new Promise((resolve) => {
                // Use password confirmation before making MFA settings changes
                confirmPassword(async (password: string) => {
                    const payloadWithPassword = { ...payload, password };
                    const response = await updateMfaToggles(payloadWithPassword);
                    //console.log("MFA update response:", response);
                    if (response.Code === 200) {
                        addToast("", "MFA settings updated successfully.");
                        resolve(true); // Indicate success to EditorControl
                    } else {
                        addToast("Error", response.Msg || "Failed to update MFA settings.");
                        resolve(false); // Indicate failure to EditorControl
                    }
                }, {
                    title: "Security Verification",
                    message: "Enter your password to update MFA settings."
                });
            });
        }

        return true; // No changes to save
    }

    if (mfaSettingsLoading) {
        return <div>Loading MFA settings...</div>;
    }

    if (mfaSettingsError) {
        return <div>Error loading MFA settings: {mfaSettingsError.toString()}</div>;
    }

    if (!mfaSettings) {
        return <div>No MFA settings found.</div>;
    }

    return <>
        <CStack header='Authentication'>
            <EditorControl defaultData={mfaSettings} onApply={handleApply} editIcon={EditIcon}>
                {(data, onChanged, edit) => {
                    return <>
                        {/* MPIN Section */}
                        <>
                            {/* MPIN Enable Toggle */}
                            <SwitchControl edit={edit} icon={DialpadIcon} text='MPIN' value={data.isPinEnabled} onChanged={val => onChanged("isPinEnabled", val)} />

                            {/* MPIN Setup/Change Area - Visible only if MPIN is enabled */}
                            {data.isPinEnabled && (
                                <ButtonControl
                                    buttonLabel='Setup'
                                    variant='outline-primary'
                                    icon={SlidersIcon}
                                    text={mfaSettings.hasPinSetup ? "Change MPIN" : "Set MPIN"}
                                    onClicked={handleOpenMpinDialog}
                                    enabled={edit} // Enable button only when in edit mode
                                />
                            )}
                        </>

                        {/* Passkey Section */}
                        {(
                            <>
                                {/* Passkey Enable Toggle */}
                                <SwitchControl
                                    edit={edit && passkeySupport.supported}
                                    icon={KeyIcon}
                                    text='Passkeys'
                                    value={data.isPasskeyEnabled}
                                    onChanged={val => onChanged("isPasskeyEnabled", val)}
                                />

                                {/* Browser Support Warning */}
                                {!passkeySupport.supported && (
                                    <div style={{
                                        marginLeft: 20,
                                        marginTop: 5,
                                        color: '#ef4444',
                                        fontSize: '0.875rem'
                                    }}>
                                        Your browser doesn't support passkeys
                                    </div>
                                )}

                                {/* Passkey action - visible only if passkeys are enabled.
                                    No passkey yet → let the user set one up immediately;
                                    otherwise → open the management dialog. */}
                                {data.isPasskeyEnabled && passkeySupport.supported && (
                                    mfaSettings.hasPasskeySetup ? (
                                        <ButtonControl
                                            buttonLabel='Manage'
                                            variant='outline-primary'
                                            icon={GearIcon}
                                            text="Manage Passkeys"
                                            onClicked={handleOpenPasskeyManagement}
                                            enabled={edit} // Enable button only when in edit mode
                                        />
                                    ) : (
                                        <ButtonControl
                                            buttonLabel='Set up'
                                            variant='outline-primary'
                                            icon={KeyIcon}
                                            text="Set up Passkey"
                                            onClicked={handleRegisterPasskey}
                                            enabled={edit} // Enable button only when in edit mode
                                        />
                                    )
                                )}
                            </>
                        )}
                        {/* TODO: Add section for Biometrics when implemented */}
                    </>;
                }}
            </EditorControl>
        </CStack>
    </>;
};

export default MfaSettingsSection;
